import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { addMilliseconds, differenceInSeconds, isFuture } from "date-fns";
import { useCallback, useRef, useState } from "react";

import {
  EXTENDED_RETRY_DURATION_MS,
  EXTENDED_RETRY_INTERVAL_MS,
  QUICK_RETRY_DURATION_MS,
  QUICK_RETRY_INTERVAL_MS,
  TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
  WORKER_NOT_CLOCKED_OUT_FROM_WORKPLACE_TIMECLOCK_ERROR_MESSAGE,
} from "./constants";

type RetryPhase = "idle" | "quick" | "extended";

interface ExtendedRetryMetadata {
  shiftId: string;
  workerId: string;
  workplaceId: string;
  action: "clock-in" | "clock-out";
}

interface UseExtendedTimekeepingRetryResult<T> {
  retryPhase: RetryPhase;
  executeWithRetry: (
    actionFunction: () => Promise<T>,
    metadata: ExtendedRetryMetadata
  ) => Promise<T | undefined>;
  cancel: () => void;
}

function isRetryableError(error: unknown): boolean {
  return (
    error instanceof Error &&
    [
      TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE,
      WORKER_NOT_CLOCKED_OUT_FROM_WORKPLACE_TIMECLOCK_ERROR_MESSAGE,
    ].includes(error.message)
  );
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function useExtendedTimekeepingRetry<T>(): UseExtendedTimekeepingRetryResult<T> {
  const [retryPhase, setRetryPhase] = useState<RetryPhase>("idle");

  const cancelledRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const cancel = useCallback(() => {
    cancelledRef.current = true;

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
    setRetryPhase("idle");
  }, []);

  const executeWithRetry = useCallback(
    async (
      actionFunction: () => Promise<T>,
      metadata: ExtendedRetryMetadata
    ): Promise<T | undefined> => {
      cancelledRef.current = false;

      const startTime = new Date();
      let attemptNumber = 0;
      let phase: RetryPhase = "quick";

      setRetryPhase(phase);

      let retryDuration = QUICK_RETRY_DURATION_MS;
      let retryInterval = QUICK_RETRY_INTERVAL_MS;

      while (isFuture(addMilliseconds(startTime, retryDuration))) {
        if (cancelledRef.current) {
          setRetryPhase("idle");
          return undefined;
        }

        attemptNumber += 1;

        try {
          // eslint-disable-next-line no-await-in-loop
          const result = await actionFunction();
          setRetryPhase(phase);

          return result;
        } catch (error: unknown) {
          if (!isRetryableError(error)) {
            setRetryPhase("idle");
            throw error;
          }

          logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_ATTEMPT, {
            shiftId: metadata.shiftId,
            workerId: metadata.workerId,
            workplaceId: metadata.workplaceId,
            action: metadata.action,
            attemptNumber,
            phase,
            elapsedTimeInSeconds: differenceInSeconds(new Date(), startTime),
          });

          if (phase === "quick" && !isFuture(addMilliseconds(startTime, retryDuration))) {
            retryDuration = EXTENDED_RETRY_DURATION_MS + QUICK_RETRY_DURATION_MS;
            retryInterval = EXTENDED_RETRY_INTERVAL_MS;

            phase = "extended";
            setRetryPhase(phase);
          }

          if (isFuture(addMilliseconds(startTime, retryDuration - retryInterval))) {
            // eslint-disable-next-line no-await-in-loop
            await sleep(retryInterval);
          }
        }
      }

      logEvent(APP_V2_USER_EVENTS.TIMEKEEPING_INTEGRATION_RETRY_EXHAUSTED, {
        shiftId: metadata.shiftId,
        workerId: metadata.workerId,
        workplaceId: metadata.workplaceId,
        action: metadata.action,
        totalAttempts: attemptNumber,
        totalElapsedTimeInSeconds: differenceInSeconds(new Date(), startTime),
      });

      setRetryPhase("idle");
      throw new Error(TIMEKEEPING_INTEGRATIONS_ACTION_NOT_FOUND_ERROR_MESSAGE);
    },
    []
  );

  return {
    retryPhase,
    executeWithRetry,
    cancel,
  };
}
